import { httpsCallable } from "firebase/functions"

import { PluginApprovalStatus } from "@fuse/recoil/plugins"

import { firebaseFunctions, functionsAccessor } from "@firebaseConfig/clientApp"

/**
 * Type definition for the response from the listPlugins API call
 */
export interface ListPluginsApiResponseType {
  plugins: Array<{
    id: string
    code?: {
      frontendHostConfigCodeWeb?: string
      frontendGuestCodeWeb?: string
      frontendHostViewCodeWeb?: string
      serverCode?: string
      transpiledFrontendHostConfigCodeWeb?: string
      transpiledFrontendGuestCodeWeb?: string
      transpiledFrontendHostViewCodeWeb?: string
      transpiledServerCode?: string
    }
    createdAt: {
      _seconds: number
      _nanoseconds: number
    }
    createdBy: string
    enabled: boolean
    manifest: {
      author?: string
      description?: string
      eventId?: string
      hooks?: string[]
      name?: string
      requestedData?: string[]
      searchTags?: string[]
      triggers?: string[]
      version?: string
      requiresZeroConfiguration?: boolean
      requiresAuthentication?: boolean
      approvalStatus?: PluginApprovalStatus
      iconName?: string
    }
    updatedAt: {
      _seconds: number
      _nanoseconds: number
    }
    updatedBy: string
    versions: any[]
    [key: string]: any
  }>
  pagination: {
    hasMore: boolean
    lastVisible: string | null
  }
}

/**
 * Get plugin details including instance info for an event
 *
 * @param pluginId - The ID of the plugin to fetch
 * @param eventId - The ID of the event (optional)
 * @returns Plugin details, instance info, and connection status
 */
export const getPluginDetails = async ({
  eventId,
  pluginId,
}: {
  eventId?: string
  pluginId: string
}) => {
  const callId = Math.random().toString(36).substring(7) // Generate unique call ID

  console.log(`[PLUGIN] getPluginDetails API called (${callId}):`, {
    pluginId,
    eventId,
    callId,
    timestamp: new Date().toISOString(),
    stackTrace: new Error().stack?.split("\n").slice(1, 8).join("\n"), // Show call stack
  })

  try {
    // Validate required parameters
    if (!pluginId) {
      console.error(
        `[PLUGIN] getPluginDetails error (${callId}): Plugin ID is required`,
      )
      return {
        success: false,
        error: "Plugin ID is required",
      }
    }

    // Log before API call
    console.log(
      `[PLUGIN] getPluginDetails making Firebase API request (${callId}):`,
      {
        pluginId,
        eventId,
        endpoint: "pluginManagement-getPluginDetails",
        callId,
        timestamp: new Date().toISOString(),
      },
    )

    // Call the Firebase function
    const getPluginDetailsFunction = functionsAccessor.httpsCallable(
      "pluginManagement-getPluginDetails",
    )

    const startTime = Date.now()
    const result = await getPluginDetailsFunction({ eventId, pluginId })
    const endTime = Date.now()

    console.log(
      `[PLUGIN] getPluginDetails API response received (${callId}):`,
      {
        pluginId,
        eventId,
        callId,
        duration: `${endTime - startTime}ms`,
        success: true,
        hasData: !!result.data,
        dataType: typeof result.data,
        hasPlugin: !!(result.data as any)?.plugin,
        pluginName: (result.data as any)?.plugin?.manifest?.name || "unknown",
        hasGuestCode: !!(result.data as any)?.plugin?.code
          ?.transpiledFrontendGuestCodeWeb,
        hasHostCode: !!(result.data as any)?.plugin?.code
          ?.transpiledFrontendHostViewCodeWeb,
        userRole: (result.data as any)?.userRole || "unknown",
        responseDataKeys: result.data ? Object.keys(result.data as any) : [],
        timestamp: new Date().toISOString(),
      },
    )

    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    // Enhanced error logging
    console.error(`[PLUGIN] getPluginDetails error (${callId}):`, {
      pluginId,
      eventId,
      callId,
      error:
        error instanceof Error
          ? {
              message: error.message,
              name: error.name,
              stack: error.stack,
            }
          : error,
      timestamp: new Date().toISOString(),
    })

    // If the error looks like an authentication error, provide a more helpful message
    const errorMessage =
      error instanceof Error ? error.message : "Unknown error occurred"
    const isAuthError =
      errorMessage.toLowerCase().includes("auth") ||
      errorMessage.toLowerCase().includes("unauthenticated")

    console.log(`[PLUGIN] getPluginDetails error analysis (${callId}):`, {
      isAuthError,
      originalError: errorMessage,
      callId,
      timestamp: new Date().toISOString(),
    })

    return {
      success: false,
      error: errorMessage,
      isAuthError: isAuthError,
    }
  }
}

/**
 * Create or update a plugin instance for an event
 *
 * @param eventId - The ID of the event
 * @param pluginId - The ID of the plugin
 * @param configuration - Configuration data for the plugin instance
 * @param enabled - Whether the plugin is enabled
 */
export const createOrUpdatePluginInstance = async ({
  eventId,
  pluginId,
  configuration,
  enabled,
}: {
  eventId: string
  pluginId: string
  configuration: any
  enabled: boolean
}) => {
  try {
    // Update function name
    const updatePluginInstanceFunction = functionsAccessor.httpsCallable(
      "pluginManagement-updatePluginInstance",
    )
    const result = await updatePluginInstanceFunction({
      eventId,
      pluginId,
      configuration,
      enabled,
    })
    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error("Error updating plugin instance:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Disconnect a plugin from an event (delete the plugin instance)
 *
 * @param instanceId - The ID of the plugin instance
 */
export const disconnectPlugin = async ({
  instanceId,
}: {
  instanceId: string
}) => {
  try {
    // Update function name
    const disconnectPluginFunction = functionsAccessor.httpsCallable(
      "pluginManagement-disconnectPlugin",
    )
    const result = await disconnectPluginFunction({ instanceId })
    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error("Error disconnecting plugin:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Get plugin code and details
 *
 * @param pluginId - The ID of the plugin
 */
export const getPluginCode = async ({ pluginId }: { pluginId: string }) => {
  try {
    // Update function name
    const getPluginCodeFunction = functionsAccessor.httpsCallable(
      "pluginManagement-getCode",
    )
    const result = await getPluginCodeFunction({ pluginId })
    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error("Error getting plugin code:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * List available plugins with optional filtering
 *
 * @param filters - Optional filters for the plugin list
 */
export const listPlugins = async (filters?: {
  isEnabled?: boolean
  searchTerm?: string
  tags?: string[]
  eventId?: string
}): Promise<{
  success: boolean
  data?: ListPluginsApiResponseType
  error?: string
}> => {
  try {
    // Call the function
    const listPluginsFunction = functionsAccessor.httpsCallable(
      "pluginManagement-listPlugins",
    )
    const result = await listPluginsFunction(filters || {})

    // Return successful response
    return {
      success: true,
      data: result.data as ListPluginsApiResponseType,
    }
  } catch (error) {
    // Handle errors
    console.error("Error listing plugins:", error)

    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Create, update, or publish a plugin
 *
 * @param operation - Operation to perform (create, update, publish)
 * @param manifest - Plugin manifest with metadata
 * @param code - Plugin code (for create and update operations)
 * @param pluginId - Plugin ID (required for update and publish)
 * @param changelog - Optional changelog for publish operation
 */
export const managePlugin = async ({
  operation,
  manifest,
  code,
  pluginId,
  changelog,
}: {
  operation: "create" | "update" | "publish"
  manifest: {
    name: string
    description: string
    version: string
    author: string
    requestedData?: string[]
    hooks?: string[]
    triggers?: string[]
    searchTags?: string[]
    requiresZeroConfiguration?: boolean
    requiresAuthentication?: boolean
    approvalStatus?: PluginApprovalStatus
    iconName?: string
  }
  code?: {
    frontendGuestCodeWeb?: string
    frontendHostViewCodeWeb?: string
    frontendHostConfigCodeWeb?: string
    serverCode?: string
    transpiledFrontendGuestCodeWeb?: string
    transpiledFrontendHostViewCodeWeb?: string
    transpiledFrontendHostConfigCodeWeb?: string
    transpiledServerCode?: string
  }
  pluginId?: string
  changelog?: string
}) => {
  try {
    // Update function name
    const managePluginFunction = functionsAccessor.httpsCallable(
      "pluginManagement-managePlugin",
    )
    const result = await managePluginFunction({
      operation,
      manifest,
      code,
      pluginId,
      changelog,
    })

    // Type the response data
    const responseData = result.data as { success: boolean; error?: string }

    // Check if the result indicates an error
    if (responseData.success === false) {
      return {
        success: false,
        error: responseData.error || "Unknown error occurred",
      }
    }

    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error(`Error during ${operation} plugin operation:`, error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Instantly add a zero-config, lets-hang-owned plugin to an event
 *
 * @param eventId - The ID of the event
 * @param pluginId - The ID of the plugin
 */
export const instantAddPlugin = async ({
  eventId,
  pluginId,
}: {
  eventId: string
  pluginId: string
}) => {
  try {
    const updateEventPluginConnectionFunction = functionsAccessor.httpsCallable(
      "pluginManagement-updateEventPluginConnection",
    )
    const result = await updateEventPluginConnectionFunction({
      operationType: "INSTANT_ADD_PLUGIN",
      eventId,
      pluginId,
      userRole: "HOST", // Only hosts can add plugins
    })
    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error("Error instantly adding plugin:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Instantly remove a zero-config, lets-hang-owned plugin from an event
 *
 * @param eventId - The ID of the event
 * @param pluginId - The ID of the plugin
 */
export const instantRemovePlugin = async ({
  eventId,
  pluginId,
}: {
  eventId: string
  pluginId: string
}) => {
  try {
    const updateEventPluginConnectionFunction = functionsAccessor.httpsCallable(
      "pluginManagement-updateEventPluginConnection",
    )
    const result = await updateEventPluginConnectionFunction({
      operationType: "INSTANT_REMOVE_PLUGIN",
      eventId,
      pluginId,
      userRole: "HOST", // Only hosts can remove plugins
    })
    return {
      success: true,
      data: result.data,
    }
  } catch (error) {
    console.error("Error instantly removing plugin:", error)
    return {
      success: false,
      error: error instanceof Error ? error.message : "Unknown error occurred",
    }
  }
}

/**
 * Warm up the pluginManagement-updateEventPluginConnection API by calling it with no data.
 * This is used to reduce cold start latency when the real call is made later.
 * Always returns { success: true } (never throws).
 */
export const warmupUpdateEventPluginConnection = async () => {
  try {
    const updateEventPluginConnectionFunction = functionsAccessor.httpsCallable(
      "pluginManagement-updateEventPluginConnection",
    )
    // Call with empty object (no required params)
    await updateEventPluginConnectionFunction({})
    return { success: true }
  } catch (error) {
    // Swallow all errors, just return success
    return { success: true }
  }
}

/**
 * Warm up the pluginManagement-updatePluginInstance API by calling it with warmup flag.
 * This is used to reduce cold start latency when the real call is made later.
 * Always returns { success: true } (never throws).
 */
export const warmupUpdatePluginInstance = async () => {
  try {
    const updatePluginInstanceFunction = functionsAccessor.httpsCallable(
      "pluginManagement-updatePluginInstance",
    )
    // Call with warmup flag
    await updatePluginInstanceFunction({ warmup: true })
    return { success: true }
  } catch (error) {
    // Swallow all errors, just return success
    return { success: true }
  }
}
